import React, { useState } from 'react';
import { Mail, Check, Heart, Shield, Star, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import subscriptionApi from '../services/subscriptionApi';

const SubscribePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    interests: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const interestOptions = [
    { id: 'sermons', label: 'Weekly Sermons', description: 'Get notified about new biblical teachings' },
    { id: 'bible_studies', label: 'Bible Studies', description: 'Deep dive into Scripture study programs' },
    { id: 'prayer_support', label: 'Prayer Updates', description: 'Prayer requests and spiritual encouragement' },
    { id: 'ministry_news', label: 'Ministry Updates', description: 'Latest news and announcements' },
    { id: 'community_events', label: 'Community Events', description: 'Outreach and fellowship opportunities' }
  ];

  const benefits = [
    { icon: Star, title: 'Weekly Spiritual Insights', description: 'Receive biblical teaching and spiritual growth content every week' },
    { icon: Heart, title: 'Prayer & Encouragement', description: 'Get prayer support and encouragement during challenging times' },
    { icon: Shield, title: 'Ministry Updates', description: 'Stay informed about our latest ministry initiatives and community outreach' },
    { icon: Mail, title: 'Exclusive Content', description: 'Access to subscriber-only devotionals and spiritual resources' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInterestChange = (interestId) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(id => id !== interestId)
        : [...prev.interests, interestId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const result = await subscriptionApi.subscribe(
        formData.email, 
        formData.name, 
        'general', 
        formData.interests
      );
      
      console.log('Subscription successful:', result);
      
      // Navigate to thank you page for conversion tracking
      navigate('/subscribe/thank-you', { 
        state: { 
          email: formData.email, 
          name: formData.name,
          subscriptionId: result.id 
        } 
      });
      
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Subscription error:', err);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-[#f17b21] to-[#ff9649]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-display">
              Join Our Ministry Family
            </h1>
            <p className="text-lg lg:text-xl text-white/90 max-w-3xl mx-auto font-sans">
              Stay connected with El Bethel Ministries and receive weekly spiritual insights, 
              biblical teaching, and ministry updates that will strengthen your faith journey.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Subscription Form */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 font-display">
                  Subscribe for Free
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className="w-full px-4 py-3 lg:py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      className="w-full px-4 py-3 lg:py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-4 font-sans">
                      What would you like to receive? (Select all that apply)
                    </label>
                    <div className="space-y-3">
                      {interestOptions.map((option) => (
                        <label key={option.id} className="flex items-start space-x-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.interests.includes(option.id)}
                            onChange={() => handleInterestChange(option.id)}
                            className="w-5 h-5 text-[#f17b21] border-gray-300 rounded focus:ring-[#f17b21] mt-1"
                          />
                          <div>
                            <p className="font-semibold text-gray-800 font-sans">{option.label}</p>
                            <p className="text-sm text-gray-600 font-sans">{option.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-red-600 text-sm font-sans">{error}</p>
                    </div>
                  )}

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 text-green-800">
                      <Shield className="w-5 h-5" />
                      <p className="text-sm font-semibold font-sans">Privacy Promise</p>
                    </div>
                    <p className="text-sm text-green-700 mt-1 font-sans">
                      Your email is safe with us. We never spam or share your information. Unsubscribe anytime with one click.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#f17b21] to-[#ff9649] hover:from-[#d86919] hover:to-[#e8853a] text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed font-sans flex items-center justify-center space-x-3"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin w-6 h-6 border-3 border-white border-t-transparent rounded-full"></div>
                        <span>Subscribing...</span>
                      </>
                    ) : (
                      <>
                        <Mail className="w-6 h-6" />
                        <span>Subscribe for Free</span>
                        <ArrowRight className="w-6 h-6" />
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* Benefits */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 font-display">
                    Why Subscribe?
                  </h3>
                  <div className="space-y-6">
                    {benefits.map((benefit, index) => {
                      const IconComponent = benefit.icon;
                      return (
                        <div key={index} className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-[#f17b21] rounded-full flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="text-lg font-bold text-gray-800 mb-2 font-display">{benefit.title}</h4>
                            <p className="text-gray-600 leading-relaxed font-sans">{benefit.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Testimonial */}
                <div className="bg-gray-50 rounded-2xl p-6 lg:p-8">
                  <h4 className="text-xl font-bold text-gray-800 mb-4 font-display">Join the Community</h4>
                  <blockquote className="italic text-gray-700 text-center font-sans mb-4">
                    "The weekly spiritual insights have been such a blessing to my family. 
                    Thank you for faithfully sharing God's Word!"
                  </blockquote>
                  <p className="text-center text-sm text-gray-500 mt-2 font-sans">- Sarah M., Subscriber</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SubscribePage;